import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { AuthContext } from './AuthContext';

const BACKEND_URL   = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const POLL_INTERVAL = 15_000; // 15 seconds

export const NotificationContext = createContext({
  notifications:  [],
  unreadCount:    0,
  markRead:       () => {},
  markAllRead:    () => {},
});

export const NotificationProvider = ({ children }) => {
  const { isLoggedIn, authToken } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const intervalRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!authToken) return;
    try {
      const res  = await fetch(`${BACKEND_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count   || 0);
    } catch {
      // Network errors are silently ignored — notifications are non-critical
    }
  }, [authToken]);

  useEffect(() => {
    if (!isLoggedIn || !authToken) {
      setNotifications([]);
      setUnreadCount(0);
      clearInterval(intervalRef.current);
      return;
    }
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, POLL_INTERVAL);

    // Pause polling when the tab is hidden to save resources
    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(intervalRef.current);
      } else {
        fetchNotifications();
        intervalRef.current = setInterval(fetchNotifications, POLL_INTERVAL);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isLoggedIn, authToken, fetchNotifications]);

  const markRead = useCallback(async (id) => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await fetch(`${BACKEND_URL}/api/notifications/${id}/read`, {
        method:  'PATCH',
        headers: { Authorization: `Bearer ${authToken}` },
      });
    } catch {
      // Revert if server call failed
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, read: false } : n))
      );
      setUnreadCount(prev => prev + 1);
    }
  }, [authToken]);

  const markAllRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await fetch(`${BACKEND_URL}/api/notifications/read-all`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
    } catch {
      // Re-fetch to get the real state on failure
      fetchNotifications();
    }
  }, [authToken, fetchNotifications]);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markRead, markAllRead }}>
      {children}
    </NotificationContext.Provider>
  );
};
